/**
 * The contract a Replace Contract flow addresses.
 *
 * With concurrent contracts, the replace form must describe the contract being
 * replaced — not the person view, which is the primary's (ADR-156 §2). Every
 * guard and default on the form (the already-scheduled renewal, the cutover
 * floor, the ingestion-ownership warning, the scheduled-end withdrawal, and
 * the successor's pre-filled terms) is read from the entry returned here.
 *
 * @param {Object|null} employee  GET /employees/:id payload (carries contracts[])
 * @param {string|null} requestedEpisodeId  the contract the flow was launched for
 *   (?contract= in the URL, else router state); null = the person's primary.
 * @returns {{ contract: Object|null, notFound: boolean }}
 *   contract  the contracts[] entry being replaced; null when the payload has no
 *             contracts[] (callers fall back to the flat person fields).
 *   notFound  true when a specific contract was requested and it is not an open
 *             contract of this person — the flow must refuse rather than guess.
 */
export const resolveReplaceTarget = (employee, requestedEpisodeId = null) => {
  const contracts = Array.isArray(employee?.contracts) ? employee.contracts : [];
  const open = contracts.filter((contract) => !contract.endDate);

  if (requestedEpisodeId) {
    const match = open.find((contract) => contract.id === requestedEpisodeId) || null;
    return { contract: match, notFound: !match };
  }

  const primary = open.find((contract) => contract.isPrimary) || open[0] || null;
  return { contract: primary, notFound: false };
};

/**
 * The replace-form facts of the target contract, in the shape the form keeps.
 * Falls back to the flat person fields when the payload has no contracts[]
 * (pre-ADR-156 payloads), which is the previous behaviour.
 */
export const replaceTargetFacts = (employee, contract) => {
  if (!contract) {
    return {
      episodeId: employee?.primaryEmploymentEpisodeId || employee?.currentEmploymentEpisodeId || null,
      startDate: employee?.contractStartDate || employee?.hireDate || null,
      source: employee?.currentEpisodeSource || null,
      pendingRenewalOn: employee?.pendingRenewal?.effectiveOn || null,
      hasPendingResignation: Boolean(employee?.hasPendingResignation),
      contractTypeId: employee?.contractType?.id || '',
      contractCompanyId: employee?.contractCompany?.id || '',
      engagementModelId: employee?.engagementModel?.id || '',
    };
  }
  return {
    episodeId: contract.id,
    startDate: contract.startDate || null,
    source: contract.source || null,
    pendingRenewalOn: contract.pendingRenewal?.effectiveOn || null,
    hasPendingResignation: Boolean(contract.hasPendingResignation),
    contractTypeId: contract.contractType?.id || '',
    contractCompanyId: contract.contractCompany?.id || '',
    engagementModelId: contract.engagementModel?.id || '',
  };
};

const ymdOf = (value) => (value ? String(value).slice(0, 10) : null);
const ddmmyyyy = (ymd) => ymd.split('-').reverse().join('/');

/**
 * ADR-188 D4/D5 — what the replace form must know about where the replaced
 * contract sits: a SECONDARY's successor is a secondary too (bounded by the
 * primary's scheduled end, like Add Contract), and replacing it changes no
 * person fact (hire.re-hire ignores them for a non-primary predecessor).
 *
 * @param {Object|null} employee  GET /employees/:id payload
 * @param {Object|null} contract  resolveReplaceTarget(...).contract
 * @returns {{ isSecondary: boolean, ownEndYmd: string|null, primaryEndYmd: string|null }}
 */
export const replaceTargetBounds = (employee, contract) => {
  const isSecondary = Boolean(contract && !contract.isPrimary);
  const primary = (employee?.contracts || []).find((c) => c.isPrimary && !c.endDate) || null;
  return {
    isSecondary,
    ownEndYmd: ymdOf(contract?.termEndDate),
    primaryEndYmd: isSecondary ? ymdOf(primary?.termEndDate) : null
  };
};

/**
 * ADR-188 D4 (a)/(b) — the refusal the server would give for this successor,
 * worded the same; null when it fits.
 */
export const replaceSuccessorProblem = (bounds, { startYmd = null, endYmd = null } = {}) => {
  const P = bounds?.isSecondary ? bounds.primaryEndYmd : null;
  if (!P) {
    return null;
  }
  if (startYmd && startYmd >= P) {
    return `The new contract would start on or after the primary contract ends (${ddmmyyyy(P)})`;
  }
  if (!endYmd) {
    return `The primary contract ends on ${ddmmyyyy(P)}: the new contract needs an end date on or before it`;
  }
  if (endYmd > P) {
    return `The new contract cannot end after the primary contract (${ddmmyyyy(P)})`;
  }
  return null;
};

/**
 * ADR-188 D4 (c) — a cutover after the replaced contract's own scheduled end
 * is a GAPPED replacement (a designed case): warned, never refused.
 */
export const replaceGapWarning = (bounds, cutoverYmd) => (
  bounds?.ownEndYmd && cutoverYmd && cutoverYmd > bounds.ownEndYmd
    ? `This contract ends on ${ddmmyyyy(bounds.ownEndYmd)} — a cutover after it leaves a gap (gapped replacement)`
    : null
);
